// src/components/insurance/InsurancesTable.tsx
import React from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { InsuranceData, ColumnKey } from '../../pages/Insurance';

interface InsurancesTableProps {
  paginatedData: InsuranceData[];
  visibleColumns: ColumnKey[];
  getColumnDisplayName: (col: ColumnKey) => string;
  getSortIndicator: (key: ColumnKey) => string;
  handleSort: (key: ColumnKey) => void;
  columnFilters: Partial<Record<ColumnKey, string>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<Partial<Record<ColumnKey, string>>>>;
  selectedRows: string[];
  handleSelectAll: () => void;
  handleRowSelect: (id: string) => void;
  handleEdit: (insurance: InsuranceData) => void;
  handleDelete: (id: string) => void;
  formatCurrency: (value: string | number) => string;
  formatDate: (date: string) => string;
  getLabel: (group: string, value: string) => string;
  lookupGroupMap: Record<string, string>;
}

const InsurancesTable: React.FC<InsurancesTableProps> = (props) => {
  const {
    paginatedData, visibleColumns, getColumnDisplayName, getSortIndicator, handleSort,
    columnFilters, setColumnFilters, selectedRows, handleSelectAll, handleRowSelect,
    handleEdit, handleDelete, formatCurrency, formatDate, getLabel, lookupGroupMap
  } = props;

  return (
    <div className="overflow-x-auto">
      <table className="w-full divide-y divide-gray-200 min-w-max">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-6 py-3">
              <input type="checkbox" checked={selectedRows.length === paginatedData.length && paginatedData.length > 0} onChange={handleSelectAll} className="rounded text-pink-600 focus:ring-pink-600" />
            </th>
            {visibleColumns.map(col => (
              <th key={col} className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100">
                <div className="flex items-center justify-between" onClick={() => handleSort(col)}>
                  <span>{getColumnDisplayName(col)}</span>
                  <span className="text-xs">{getSortIndicator(col)}</span>
                </div>
                <input
                  type="text"
                  placeholder="Filter..."
                  value={columnFilters[col] || ''}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setColumnFilters(prev => ({ ...prev, [col]: e.target.value }))}
                  className="mt-1 w-full p-1 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-pink-600"
                />
              </th>
            ))}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {paginatedData.map(insurance => (
            <tr key={insurance.id} className={`hover:bg-gray-50 ${selectedRows.includes(insurance.id) ? 'bg-pink-50' : ''}`}>
              <td className="px-6 py-4">
                <input type="checkbox" checked={selectedRows.includes(insurance.id)} onChange={() => handleRowSelect(insurance.id)} className="rounded text-pink-600 focus:ring-pink-600" />
              </td>
              {visibleColumns.map(col => (
                <td key={col} className="px-6 py-4 whitespace-nowrap text-sm">
                  { lookupGroupMap[col] 
                    ? getLabel(lookupGroupMap[col], insurance[col as keyof InsuranceData] as string)
                    : ['totalInsuranceAmount', 'insuranceInstallmentAmount'].includes(col)
                    ? <span className="text-pink-600 font-medium">{formatCurrency(insurance[col as keyof InsuranceData] as string)}</span>
                    : ['insuranceStartDate', 'insuranceEndDate'].includes(col)
                    ? formatDate(insurance[col as keyof InsuranceData] as string)
                    : insurance[col as keyof InsuranceData] || 'N/A'
                  }
                </td>
              ))}
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <button onClick={() => handleEdit(insurance)} className="text-pink-600 hover:text-pink-800 p-1" title="Edit"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(insurance.id)} className="text-red-600 hover:text-red-800 p-1" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InsurancesTable;